import type { TFunction } from 'i18next';

/**
 * Season-division: a chosen stretch is described by a `segmentScheme` plus a
 * 0-based `segmentIndex`. This is the canonical label used wherever a member's
 * chosen segment is shown (membership members overview, course enrollments).
 *
 * Pure & side-effect free so it can be reused without a React tree.
 */
export interface SegmentInfo {
  segmentScheme?: string | null;
  segmentIndex?: number | null;
}

/**
 * i18n unit noun for a division scheme (e.g. "quarter" for `quarters`). Falls
 * back to the generic "part" for unknown / custom schemes.
 */
export const segmentUnit = (t: TFunction, scheme: string): string => {
  switch (scheme) {
    case 'halves':
      return t('memberships.unitHalf');
    case 'thirds':
      return t('memberships.unitThird');
    case 'quarters':
      return t('memberships.unitQuarter');
    default:
      return t('memberships.unitPart');
  }
};

/**
 * Human label for a chosen stretch, or `null` when no segment is set (e.g. a
 * per-session / not-yet-bought enrollee). Returns `t('memberships.wholeSeason')`
 * for the `'full'` scheme, else e.g. "1. quarter".
 *
 * `segmentIndex` is guarded with `Number()` in case it arrives as a string.
 */
export const segmentLabelText = (t: TFunction, info: SegmentInfo): string | null => {
  if (!info.segmentScheme) return null;
  if (info.segmentScheme === 'full') return t('memberships.wholeSeason');
  const idx = Number(info.segmentIndex ?? 0) + 1;
  return t('memberships.segmentLabel', { index: idx, unit: segmentUnit(t, info.segmentScheme) });
};
